import IconUser16 from '@warp-ds/icons/react/user-16';
import type { ToggleEntry } from '@warp-ds/react/components/toggle';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SimpleHead } from 'shared/page/Head';
import { Main } from 'shared/page/Main';
import { ResultPageLayout } from 'shared/page/result/ResultPageLayout';
import { Sorting } from 'shared/page/result/Sorting';
import { Dropdown } from 'shared/search/common/Dropdown';
import { PaxCombination } from 'shared/search/prepackage/Pax/PaxCombination';
import { formatPaxTriggerLabel } from 'shared/search/prepackage/Pax/PaxUtils';
import type { PaleState, ThunkDispatchFunc } from '@/interfaces';
import displayValues from '@/resources/displayValues.json';
import filterActions, { actions } from '@/state/filterActions';
import { fetchResults } from '@/state/resultActions';
import { DestinationFilter } from './filters/checkbox/DestinationFilter';
import CurrentFilters from './filters/currentFilters/CurrentFilters';
import { FilterGroup } from './filters/FilterGroup';
import { Filters } from './filters/Filters';
import { OriginDropdown } from './filters/origin/OriginDropdown';
import ResultTravelTimePicker from './filters/travelTime/ResultTravelTimePicker';
import { ResultList } from './resultList/ResultList';

const sorting: Record<string, string> = displayValues.sorting;

export const ResultPage = () => {
    const dispatch = useDispatch<ThunkDispatchFunc>();

    const numberOfAdults = useSelector((s: PaleState) => s.filters.numberOfAdults);
    const agesOfChildren = useSelector((s: PaleState) => s.filters.agesOfChildren);
    const origin = useSelector((s: PaleState) => s.filters.origin);
    const numberOfOffers = useSelector((state: PaleState) => state.results.numberOfHotels);
    const sortOptions = useSelector((state: PaleState) => state.results.sortOptions);
    const defaultSort = useSelector((state: PaleState) => state.results.defaultSort);
    const selectedSort = useSelector((state: PaleState) => state.filters.sort);

    const validSortOptions = (sortOptions ?? []).filter((option) => Boolean(sorting[option]));
    const sortToggleOptions: ToggleEntry[] = validSortOptions.map((option) => ({
        label: sorting[option],
        value: option,
    }));

    const onSortChange = useCallback(
        (sort: string | undefined): void => {
            dispatch(filterActions.changeSort(sort));
            dispatch(fetchResults());
        },
        [dispatch],
    );

    const updatePax = useCallback(
        (adults: number, ages: number[]) => {
            dispatch(actions.changePaxCombination({ numberOfAdults: adults, agesOfChildren: ages }));
            dispatch(fetchResults());
        },
        [dispatch],
    );

    const changeOrigin = useCallback(
        (newOrigin: string) => {
            dispatch(actions.changeOrigin(newOrigin));
            dispatch(fetchResults());
        },
        [dispatch],
    );

    const handleFilterChange = useCallback(() => {
        dispatch(fetchResults());
    }, [dispatch]);

    return (
        <>
            <SimpleHead
                title="Pakkereiser - sammenlign pris | FINN reise"
                canonical="https://www.finn.no/reise/pakkereiser/resultat"
                description="Finn billige pakkereiser fra alle reisebyrå på ett sted! På FINN.no kan du sammenligne priser og spare penger."
            />
            <Main broadcastUrl="/broadcasts?path=www.finn.no/reise/pakkereiser">
                <ResultPageLayout
                    title="Pakkereiser"
                    breadcrumbs={{ path: 'pakkereiser' }}
                    filters={
                        <>
                            <div test-id="origin" className="p-16 pt-0 md:pt-12">
                                <h3 className="h4 mb-16">Utreise og dato</h3>
                                <OriginDropdown selectedValue={origin} filterChange={changeOrigin} />
                                <h3 className="sr-only">Alternativer for avreise og hjemreise</h3>
                                <ResultTravelTimePicker />
                                <Dropdown
                                    trigger={{
                                        text: formatPaxTriggerLabel(numberOfAdults, agesOfChildren),
                                        label: 'Reisende',
                                    }}
                                    prefix={<IconUser16 aria-hidden />}
                                    className="mt-16">
                                    <PaxCombination numberOfAdults={numberOfAdults} agesOfChildren={agesOfChildren} updatePax={updatePax} />
                                </Dropdown>
                            </div>
                            <FilterGroup title="Reisemål" testId="destination">
                                <DestinationFilter />
                            </FilterGroup>
                            <Filters onFilterChange={handleFilterChange} />
                        </>
                    }
                    sortingSection="pakkereiser"
                    sorting={
                        <Sorting
                            sortOptions={sortToggleOptions}
                            defaultSort={defaultSort}
                            selectedSort={selectedSort}
                            onChange={onSortChange}
                        />
                    }
                    numberOfOffers={numberOfOffers}>
                    <CurrentFilters />
                    <ResultList />
                </ResultPageLayout>
            </Main>
        </>
    );
};
